import React, {useState} from 'react';
import {ProviderEvent, ProviderEventType, ProviderEventTypeNameMap} from "./domain";
import {LinkToNewTabKeepContext} from "../../common";
import {ClientDocumentTypeMap, ClientProfile, CLIENT_PROFILE_PROGRESS_PORTRAITS_PATH} from "../client/domain";
import {fullName} from "../../core/domain";
import { useApplicationRootUrl } from "common/component/ApplicationRootContext";

const PREVIEW_CLIENT_LIMIT = 3;

type ExpandableClientLinksProps = {
    clients: ClientProfile[];
    markReadCallback: () => void;
    rootUrl: string;
    isSelected: boolean;
};

const ExpandableClientLinks = ({clients, markReadCallback, rootUrl, isSelected}: ExpandableClientLinksProps) => {
    const [expanded, setExpanded] = useState(false);
    const visibleClients = expanded ? clients : clients.slice(0, PREVIEW_CLIENT_LIMIT);
    const hiddenCount = clients.length - PREVIEW_CLIENT_LIMIT;
    const clientLinkClass = isSelected ? "font-bold underline text-white" : "font-bold underline text-blue-portal-link";
    const toggleClass = isSelected ? "font-bold text-white cursor-pointer" : "font-bold text-gray-500 cursor-pointer";

    return (
        <div className="flex flex-col">
            {visibleClients.map((client) => (
                <div key={client.guid}>
                    <LinkToNewTabKeepContext
                        link={`${rootUrl}/clients/${client.guid}/${CLIENT_PROFILE_PROGRESS_PORTRAITS_PATH}`}
                        callback={markReadCallback}
                        displayInline>
                        <span className={clientLinkClass}>
                            {client.fullName}
                        </span>
                    </LinkToNewTabKeepContext>
                </div>
            ))}
            {!expanded && hiddenCount > 0 && (
                <div>
                    <span
                        className={`${toggleClass} inline-block`}
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpanded(true);
                        }}>
                        +{hiddenCount} more
                    </span>
                </div>
            )}
            {expanded && clients.length > PREVIEW_CLIENT_LIMIT && (
                <div>
                    <span
                        className={`${toggleClass} inline-block`}
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpanded(false);
                        }}>
                        show less
                    </span>
                </div>
            )}
        </div>
    );
};

export const ProviderEventPreviewDescription = (
    providerEvent: ProviderEvent,
    markReadCallback: () => void,
    isSelected: boolean
) => {
    const auth = providerEvent.authorization ?? null;
    const client = providerEvent.client ?? null;
    const rootUrl = useApplicationRootUrl();
    const notificationLinkTextClass = isSelected ? `font-bold underline text-white` : `font-bold underline text-blue-portal-link`;

    switch (ProviderEventTypeNameMap.get(providerEvent.eventType)) {
        case ProviderEventType.Placement_Confirmed:
            return <span>
                Placement for {<LinkToNewTabKeepContext link={`${rootUrl}/clients/${client?.guid}/contacts`}
                                                        callback={markReadCallback}
                                                        displayInline>
                    <span className={notificationLinkTextClass}>
                        {client?.fullName}
                    </span>
            </LinkToNewTabKeepContext>} is confirmed
            </span>;
        case ProviderEventType.Authorization_Assigned:
            return <span>
                Authorization {<LinkToNewTabKeepContext
                link={`${rootUrl}/clients/${client?.guid}/authorizations?authorizationGuid=${auth?.guid}`}
                callback={markReadCallback}
                displayInline>
                        <span className={notificationLinkTextClass}>
                           {auth.bhpnAuthNumber}
                        </span>
            </LinkToNewTabKeepContext>
            } for {
                <LinkToNewTabKeepContext link={`${rootUrl}/clients/${client?.guid}/contacts`}
                                         callback={markReadCallback}
                                         displayInline>
                    <span className={notificationLinkTextClass}>
                        {client?.fullName}
                    </span>
                </LinkToNewTabKeepContext>
            } has been received
            </span>;
        case ProviderEventType.Authorization_Updated:
            return <span>
                Authorization {<LinkToNewTabKeepContext
                link={`${rootUrl}/clients/${client?.guid}/authorizations?authorizationGuid=${auth?.guid}`}
                callback={markReadCallback}
                displayInline>
                        <span className={notificationLinkTextClass}>
                           {auth.bhpnAuthNumber}
                        </span>
            </LinkToNewTabKeepContext>
            } {`${providerEvent?.authChangedSections || ''} `}have been updated for {
                <LinkToNewTabKeepContext link={`${rootUrl}/clients/${client?.guid}/contacts`}
                                         callback={markReadCallback}
                                         displayInline>
                    <span className={notificationLinkTextClass}>
                        {client?.fullName}
                    </span>
                </LinkToNewTabKeepContext>
            }
            </span>;
        case ProviderEventType.Authorization_Report_Due:
            return <span>
                ABA Progress Report for Authorization {<LinkToNewTabKeepContext
                link={`${rootUrl}/clients/${client?.guid}/authorizations?authorizationGuid=${auth?.guid}`}
                callback={markReadCallback}
                displayInline>
                        <span className={notificationLinkTextClass}>
                           {auth.bhpnAuthNumber}
                        </span>
            </LinkToNewTabKeepContext>
            } for {
                <LinkToNewTabKeepContext link={`${rootUrl}/clients/${client?.guid}/contacts`}
                                         callback={markReadCallback}
                                         displayInline>
                    <span className={notificationLinkTextClass}>
                        {client?.fullName}
                    </span>
                </LinkToNewTabKeepContext>
            } is now past due
            </span>;
        case ProviderEventType.Client_Report_Feedback:
            return <span>
                Corrections requested for {<LinkToNewTabKeepContext
                    link={`${rootUrl}/clients/${client?.guid}/clientReports`}
                    callback={markReadCallback}
                    displayInline>
                        <span className={notificationLinkTextClass}>
                           Client Report
                        </span>
                </LinkToNewTabKeepContext>
                } (submit by <span
                className="font-bold">{providerEvent.clientReport?.submittedBy ? fullName(providerEvent.clientReport?.submittedBy) : ''}</span>) associated with Authorization {
                <LinkToNewTabKeepContext
                    link={`${rootUrl}/clients/${client?.guid}/authorizations?authorizationGuid=${auth?.guid}`}
                    callback={markReadCallback}
                    displayInline
                >
                    <span className={notificationLinkTextClass}>
                        {auth?.bhpnAuthNumber}
                    </span>
                </LinkToNewTabKeepContext>
            } is now available
            </span>;
        case ProviderEventType.Client_Report_Approved:
            return <span>
                {<LinkToNewTabKeepContext
                link={`${rootUrl}/clients/${client?.guid}/clientReports`}
                callback={markReadCallback}
                displayInline>
                        <span className={notificationLinkTextClass}>
                           Client Report
                        </span>
            </LinkToNewTabKeepContext>
            } (submit by <span
                className="font-bold">{providerEvent.clientReport?.submittedBy ? fullName(providerEvent.clientReport?.submittedBy) : ''}</span>) associated with Authorization {
                <LinkToNewTabKeepContext
                    link={`${rootUrl}/clients/${client?.guid}/authorizations?authorizationGuid=${auth?.guid}`}
                    callback={markReadCallback}
                    displayInline
                >
                    <span className={notificationLinkTextClass}>
                        {auth?.bhpnAuthNumber}
                    </span>
                </LinkToNewTabKeepContext>
            } is now authorized
            </span>;
        case ProviderEventType.Client_Document_Added:
            return <span>
                Document {<LinkToNewTabKeepContext
                link={`${rootUrl}/clients/${client?.guid}/documentFiles`}
                callback={markReadCallback}
                displayInline>
                        <span className={notificationLinkTextClass}>
                           {ClientDocumentTypeMap.get(providerEvent.clientDocumentType)}
                        </span>
            </LinkToNewTabKeepContext>
            } has been added for {
                <LinkToNewTabKeepContext link={`${rootUrl}/clients/${client?.guid}/contacts`}
                                         callback={markReadCallback}
                                         displayInline>
                    <span className={notificationLinkTextClass}>
                        {client.fullName}
                    </span>
                </LinkToNewTabKeepContext>
            }
            </span>;

        case ProviderEventType.Treatment_Hold_End: {
            const caseNumber = providerEvent.caseNumber ?? '';
            const clientName = client?.fullName ?? '';
            const reminderText = providerEvent.holdReminderNumber === 2
                ? 'is today'
                : 'is in 2 days';
            return <span>
                Treatment Hold (<span className="font-bold">{caseNumber}</span>) end date for{' '}
                <span className="font-bold">{clientName}</span> {reminderText}, please confirm the end date
            </span>;
        }

        case ProviderEventType.Staffing_Plan_Required: {
            const clientName = client?.fullName ?? '';
            const caseNumber = providerEvent.caseNumber ?? '';
            const contactLink = `${rootUrl}/clients/${client?.guid}/contacts`;
            const submittedRequestLink = `${rootUrl}/clients/${client?.guid}/submittedRequest`;
            return <span>
                Action required: Submit Staffing Plan for{' '}
                <LinkToNewTabKeepContext link={contactLink}
                                         callback={markReadCallback}
                                         displayInline>
                    <span className={notificationLinkTextClass}>
                        {clientName}
                    </span>
                </LinkToNewTabKeepContext>
                {' '}(Treatment Hold Case{' '}
                <LinkToNewTabKeepContext link={submittedRequestLink}
                                         callback={markReadCallback}
                                         displayInline>
                    <span className={notificationLinkTextClass}>
                        {caseNumber}
                    </span>
                </LinkToNewTabKeepContext>)
            </span>;
        }

        case ProviderEventType.Progress_Portrait_Sync: {
            const linkedClients = providerEvent.linkedClients ?? [];
            const count = linkedClients.length;
            const isSingular = count === 1;
            return <div>
                Action Required: You have {count} new Progress Portrait{!isSingular ? 's' : ''} available for review:{' '}
                <ExpandableClientLinks
                    clients={linkedClients}
                    markReadCallback={markReadCallback}
                    rootUrl={rootUrl}
                    isSelected={isSelected}/>
            </div>;
        }
    }
}
